/**
 * `createApiSubscriptionsSource` — the subscriptions-mgmt-api port over the
 * shared `ApiClient`.
 *
 * Backs the Settings "Billing" tab: reads the caller's current subscription (the
 * billing/entitlement anchor) so the UI can reflect the plan tier, lifecycle
 * status and any per-subscription overrides. The wire is snake_case (the commons
 * `Subscription` model serialized); this adapter maps it to the camelCase summary
 * the UI renders. The canonical `/subscriptions` path is rewritten onto the
 * `.management.` zone (`/management/subscriptions-mgmt-api/v0/...`) by the
 * dashboard `rewritePath`, so region routing / auth / envelope unwrap are
 * inherited.
 *
 * Reads are pinned to the caller's own customer server-side (external callers
 * cannot pass an arbitrary `customer_id`), so no customer id is sent from here.
 */
import { isEntitledSubscription, type SubscriptionStatus, type Plan, type PlanSettingValue } from "@authe/fe-commons/plans";
import { type ApiClient } from "@authe/fe-commons/api";
import { walkPages } from "./pagedList";
import { asNullableString, asNumber, asRecord, asString, isRecord, rowsFromWire } from "./wire";

export { isEntitledStatus, isEntitledSubscription, subscriptionAccessEnd, type SubscriptionStatus } from "@authe/fe-commons/plans";

export interface SubscriptionSummary {
  id: string;
  /**
   * The plan this subscription grants (`free` / `pro` / `enterprise` / custom),
   * or `null` when the row names none. Not defaulted to `free`: the backend's
   * `plan_by_id(None)` is "no such tier", which on an entitled row is UNKNOWN
   * rather than the cheapest tier; backend paid actions require verification.
   */
  planId: string | null;
  /**
   * Lifecycle status, or `null` when the row carries none. Not defaulted to
   * `active`: a missing status is not entitled (see {@link isEntitledStatus}).
   */
  status: SubscriptionStatus | null;
  displayName: string | null;
  /** Seat / unit count for per-seat plans. */
  quantity: number;
  currency: string | null;
  /** Epoch seconds. */
  trialEnd: number | null;
  currentPeriodEnd: number | null;
  canceledAt: number | null;
  /** When true the subscription ends at `currentPeriodEnd` instead of renewing. */
  cancelAtPeriodEnd: boolean;
  /** Sparse overrides layered over the resolved plan (display-only here). */
  entitlementOverrides: Record<string, unknown>;
  quotaOverrides: Record<string, unknown>;
  /**
   * The row's own metadata bag — where a per-customer `load_profile` lives.
   *
   * Distinct from `entitlementOverrides`: that says which FEATURES this
   * customer has, this carries knobs that are not features. Both already
   * existed on the backend model; only the entitlement half was on the wire.
   */
  metadata: Record<string, unknown>;
  /**
   * The tier this row names AS THE PLAN CATALOGUE STORES IT (subscriptions-mgmt-api
   * embeds it). The tiers in `@authe/fe-commons/plans` are only defaults — an
   * operator may have edited this one, or it may be a bespoke tier this build
   * has never heard of. `null` when the API sent none (an older API, or a tier
   * the API does not know either).
   */
  plan?: Plan | null;
  /** Epoch seconds the row was created (used to pick the newest subscription). */
  createdAt: number | null;
}

/** The embedded tier, when the wire carries one that looks like a plan. */
function planFromWire(raw: unknown): Plan | null {
  const plan = asRecord(raw);
  return typeof plan.id === "string" && Array.isArray(plan.entitlements) && Array.isArray(plan.quotas)
    ? (plan as unknown as Plan)
    : null;
}

/**
 * The plan this customer is on, AS THE API DECIDED IT — `current` on the first
 * list page (subscriptions-mgmt-api `current_plan_view`).
 *
 * Built by the functions the authorizer runs, so the dashboard does not
 * re-derive a plan: which row decides, what an ended subscription leaves the
 * customer on, what a customer with no row is on, and what an operator's edit
 * to a tier changed are each answered once, server-side. Re-deriving them here
 * from the rows and the BUNDLED tiers is how a screen came to show the code's
 * tier while the gateway enforced the edited one.
 */
export interface ResolvedPlan {
  /** The row that decides, or `null` when the customer holds none. */
  subscriptionId: string | null;
  /** The tier in force (free for no row or an ended one), or a bespoke id no tier declares. */
  planId: string | null;
  /** That tier as the catalogue stores it, BEFORE this customer's exceptions: "your plan". */
  tier: Plan | null;
  /**
   * The tier with this customer's exceptions applied — what every gate, cap,
   * quota and setting reads. `null` only for a plan id no tier declares, which
   * keeps navigation available while backend paid actions require verification.
   */
  plan: Plan | null;
  /** Every plan setting, labelled by the API, with the value in force. */
  settings: PlanSettingValue[];
  /** The public tiers as stored, for comparing (never another customer's deal). */
  tiers: Plan[];
}

/** One read's answer: the deciding row, and the API's resolution of it. */
export interface CurrentSubscription {
  /** The row that decides the plan (`current_subscription`), or `null` when there is none. */
  subscription: SubscriptionSummary | null;
  /** `null` from an API that predates `current` — the caller falls back to the row. */
  resolved: ResolvedPlan | null;
}

const SETTING_KINDS: ReadonlySet<string> = new Set(["choice", "count", "choices"]);

/** `{option: name}`, keeping only string names; absent from an older API. */
function optionLabelsFromWire(raw: unknown): Record<string, string> | undefined {
  if (!isRecord(raw)) return undefined;
  const labels = Object.fromEntries(Object.entries(raw).filter((e): e is [string, string] => typeof e[1] === "string"));
  return Object.keys(labels).length ? labels : undefined;
}

function settingFromWire(raw: unknown): PlanSettingValue | null {
  const s = asRecord(raw);
  if (typeof s.key !== "string" || typeof s.kind !== "string" || !SETTING_KINDS.has(s.kind)) return null;
  return {
    key: s.key,
    kind: s.kind as PlanSettingValue["kind"],
    label: asNullableString(s.label) || s.key,
    description: asNullableString(s.description) ?? "",
    options: Array.isArray(s.options) ? s.options.filter((o): o is string => typeof o === "string") : [],
    when_unset: asNullableString(s.when_unset) ?? undefined,
    requires_entitlement: asNullableString(s.requires_entitlement) ?? undefined,
    option_labels: optionLabelsFromWire(s.option_labels),
    value: s.value ?? null,
  };
}

/** `current`, when the payload carries one; `null` otherwise (an older API). */
export function resolvedPlanFromWire(raw: unknown): ResolvedPlan | null {
  if (!isRecord(raw) || !Array.isArray(raw.tiers)) return null;
  return {
    subscriptionId: asNullableString(raw.subscription_id) || null,
    planId: asNullableString(raw.plan_id) || null,
    tier: planFromWire(raw.tier),
    plan: planFromWire(raw.plan),
    settings: (Array.isArray(raw.settings) ? raw.settings : [])
      .map(settingFromWire)
      .filter((s): s is PlanSettingValue => s !== null),
    tiers: raw.tiers.map(planFromWire).filter((p): p is Plan => p !== null),
  };
}

export interface SubscriptionsSource {
  /**
   * The caller's current subscription and the API's resolution of it.
   *
   * `subscription` is the newest entitled (active-ish) row, falling back to
   * the newest row overall, or `null` when the customer holds none — an
   * ANSWER: the read succeeded and this customer subscribes to nothing, which
   * is the free tier. A read that fails REJECTS instead, so "could not tell"
   * never looks like "free". Every page is read — the choice is made over all
   * of the customer's rows, as the authorizer's `current_subscription` makes it.
   */
  getCurrent(): Promise<CurrentSubscription>;
}

function subscriptionFromWire(raw: Record<string, unknown>): SubscriptionSummary {
  return {
    id: asString(raw.id),
    // "" is as absent as a missing key: `plan_by_id("")` finds no tier either.
    planId: asNullableString(raw.plan_id) || null,
    status: (asNullableString(raw.status) || null) as SubscriptionStatus | null,
    displayName: asNullableString(raw.display_name),
    quantity: asNumber(raw.quantity) ?? 1,
    currency: asNullableString(raw.currency),
    trialEnd: raw.trial_end == null ? null : asNumber(raw.trial_end) ?? 0,
    currentPeriodEnd: raw.current_period_end == null ? null : asNumber(raw.current_period_end) ?? 0,
    canceledAt: asNumber(raw.canceled_at),
    cancelAtPeriodEnd: Boolean(raw.cancel_at_period_end ?? false),
    entitlementOverrides: asRecord(raw.entitlement_overrides),
    quotaOverrides: asRecord(raw.quota_overrides),
    metadata: asRecord(raw.metadata),
    plan: planFromWire(raw.plan),
    createdAt: asNumber(raw.created_at),
  };
}

/** Newest-first by `createdAt` (missing timestamps sort last). */
function byNewest(a: SubscriptionSummary, b: SubscriptionSummary): number {
  return (b.createdAt ?? 0) - (a.createdAt ?? 0);
}

/**
 * The rows of one `GET /subscriptions` page.
 *
 * The route answers `{subscriptions, count, next_cursor}` (`list_subscriptions`),
 * not `{items}` — and `rowsFromWire` alone read that as NO rows, for every
 * customer. While no rows meant "unknown" that was invisible; now that no rows
 * is the free tier it would downgrade everybody. `{items}` and a bare array are
 * still read, so a route that moves to the shared list shape keeps working.
 */
export function subscriptionRowsOf(payload: unknown): Record<string, unknown>[] {
  if (isRecord(payload) && Array.isArray(payload.subscriptions)) {
    return payload.subscriptions.filter(isRecord);
  }
  return rowsFromWire(payload);
}

/** The subscription that decides the plan — `current_subscription` in the Python. */
export function currentSubscriptionOf(rows: SubscriptionSummary[]): SubscriptionSummary | null {
  const ordered = [...rows].sort(byNewest);
  return ordered.find((s) => isEntitledSubscription(s)) ?? ordered[0] ?? null;
}

export function createApiSubscriptionsSource(client: ApiClient): SubscriptionsSource {
  return {
    async getCurrent(): Promise<CurrentSubscription> {
      // Only the FIRST page carries `current`; a holder rather than a `let`,
      // which TypeScript would keep narrowed to `null` past the walk.
      const first: { resolved: ResolvedPlan | null; seen: boolean } = { resolved: null, seen: false };
      const rows = await walkPages(client, "/subscriptions", {
        rowsOf: (payload) => {
          if (!first.seen) {
            first.seen = true;
            first.resolved = isRecord(payload) ? resolvedPlanFromWire(payload.current) : null;
          }
          return subscriptionRowsOf(payload);
        },
        // Never a cached page: a plan refusal's re-read must see the row as it
        // is now.
        cacheTtlMs: 0,
        // A plan picked from some of the rows is a wrong plan, not a short list.
        requireComplete: true,
      });
      const subscriptions = rows.map(subscriptionFromWire);
      return {
        subscription: first.resolved
          ? subscriptions.find((row) => row.id === first.resolved!.subscriptionId) ?? null
          : currentSubscriptionOf(subscriptions),
        resolved: first.resolved,
      };
    },
  };
}
